import { Project, apiClient } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArtieScoreData } from "@/types/artie-score";
import { ArrowRight, Clock, MoreVertical, FileText, Film, File, Presentation, Trash2, Users, Crown } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { useState } from "react";
import { useAuthStore } from "@/stores/authStore";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { showSuccess, showError } from "@/utils/toast";
import { useIsMobile } from "@/hooks/use-mobile";

interface DashboardProjectItemProps {
  project: Project;
  score?: number;
}

export const DashboardProjectItem = ({ project, score }: DashboardProjectItemProps) => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const isMobile = useIsMobile();

  // Check if current user is owner or collaborator
  const isOwner = project.user_id === user?._id;
  const isCollaborator = !isOwner && project.collaborators?.some(c => c.user_id === user?._id);

  const { data: scoreData, isLoading } = useQuery({
    queryKey: ["artieScore", project._id],
    queryFn: async () => {
      try {
        const response = await apiClient.getArtieScoreByProject(project._id);
        return response as unknown as ArtieScoreData;
      } catch (error) {
        return null;
      }
    },
    enabled: score === undefined,
    retry: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: scripts } = useQuery({
    queryKey: ["projectScripts", project._id],
    queryFn: () => apiClient.getScriptsByProject(project._id),
    staleTime: 1000 * 60 * 5,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => apiClient.deleteProject(project._id),
    onSuccess: () => {
      showSuccess("Project deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      setShowDeleteDialog(false);
    },
    onError: (error: Error) => {
      console.error("Delete project error:", error);
      showError(error.message || "Failed to delete project. Please check your permissions.");
    },
  });

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDeleteDialog(true);
  };

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  const handleProjectClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.closest('button') || target.closest('[role="button"]')) {
      return;
    }

    let targetPath;
    if (project.status === 'draft') {
      targetPath = `/projects/create/step-2?projectId=${project._id}`;
    } else if (project.is_reviewed) {
      targetPath = `/project-v2/${project._id}`;
    } else {
      targetPath = `/initial-assessment?projectId=${project._id}`;
    }

    navigate(targetPath);
  };

  const getStatusBadge = (status: Project["status"]) => {
    switch (status) {
      case "analysis_complete":
        return {
          bg: "bg-green-50",
          text: "text-green-700",
          border: "border-green-100",
          label: "Score Ready",
          indicatorColor: "bg-green-500",
        };
      case "context_complete":
      case "script_uploaded":
        return {
          bg: "bg-blue-50",
          text: "text-blue-700",
          border: "border-blue-100",
          label: "Processing",
          indicatorColor: "bg-blue-500",
          animate: true,
        };
      case "pending":
        return {
          bg: "bg-primary/10",
          text: "text-primary",
          border: "border-primary/20",
          label: "Analyzing",
          indicatorColor: "bg-primary",
          animate: true,
        };
      case "deal_closed":
        return {
          bg: "bg-emerald-50",
          text: "text-emerald-700",
          border: "border-emerald-100",
          label: "Deal Closed",
          indicatorColor: "bg-emerald-500",
        };
      default:
        return {
          bg: "bg-secondary",
          text: "text-muted-foreground",
          border: "border-border",
          label: "Draft",
          indicatorColor: "bg-muted-foreground",
        };
    }
  };

  const getProjectIcon = (status: Project["status"]) => {
    if (status === 'deal_closed') return <Presentation className="w-4 h-4" />;
    if (status === 'analysis_complete') return <FileText className="w-4 h-4" />;
    if (status === 'pending') return <Film className="w-4 h-4" />;
    return <File className="w-4 h-4" />;
  }

  const getIconContainerStyle = (status: Project["status"]) => {
    if (status === 'deal_closed') return "bg-emerald-50 border-emerald-100 text-emerald-600";
    if (status === 'analysis_complete') return "bg-secondary border-border text-primary";
    if (status === 'pending') return "bg-secondary border-border text-muted-foreground";
    return "bg-secondary border-border text-muted-foreground";
  }

  const statusConfig = getStatusBadge(project.status);

  const getProjectStatus = (score: number) => {
    if (score >= 80) return { label: "On track", color: "text-green-500" };
    if (score >= 60) return { label: "Needs work", color: "text-yellow-500" };
    return { label: "At risk", color: "text-red-500" };
  };

  const effectiveScore = score !== undefined ? score : (scoreData?.artie_score);
  const projectStatus = effectiveScore !== undefined ? getProjectStatus(effectiveScore) : null;

  return (
    <>
      <div
        onClick={handleProjectClick}
        className="group relative bg-white hover:bg-white border hover:border-neutral-300 rounded-xl p-4 transition-all duration-300 cursor-pointer overflow-hidden flex flex-col sm:flex-row items-start sm:items-center gap-4"
      >
        {/* Status Indicator Bar (Left) */}
        <div className={`absolute left-0 top-0 bottom-0 w-1 ${statusConfig.indicatorColor} opacity-70 group-hover:opacity-100 transition-opacity`} />

        {/* Icon & Title Section */}
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <div className={`w-10 h-10 rounded-lg border flex-shrink-0 flex items-center justify-center ${getIconContainerStyle(project.status)}`}>
            {getProjectIcon(project.status)}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-foreground text-sm font-cabinet tracking-tight group-hover:text-primary transition-colors line-clamp-1">
                {project.title}
              </h3>
              {isCollaborator && (
                <div title="You are a collaborator">
                  <Users className="w-3 h-3 text-blue-500" />
                </div>
              )}
              {isOwner && project.collaborators && project.collaborators.length > 0 && (
                <div title={`${project.collaborators.length} collaborator${project.collaborators.length > 1 ? 's' : ''}`}>
                  <Crown className="w-3 h-3 text-amber-500" />
                </div>
              )}
            </div>

            <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-ibm mt-0.5">
              <Clock className="w-3 h-3" />
              <span>{formatDistanceToNow(new Date(project.updated_at), { addSuffix: true, includeSeconds: true })}</span>
            </div>
          </div>
        </div>

        {/* Description / Script Preview (Middle - Hidden on very small screens) */}
        <div className="hidden md:block flex-[2] min-w-0 px-4 border-l border-border/50 h-10 flex items-center">
          {scripts && scripts.length > 0 && scripts[0].extracted_text ? (
            <p className="text-[10px] text-muted-foreground/70 font-mono line-clamp-2">
              {scripts[0].extracted_text.split('\n').slice(0, 2).join(' ')}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground font-ibm line-clamp-2 leading-relaxed">
              {project.description || "No description provided."}
            </p>
          )}
        </div>

        {/* Actions & Status (Right) */}
        <div className="flex items-center justify-between sm:justify-end gap-4 w-full sm:w-auto mt-2 sm:mt-0 pt-2 sm:pt-0 sm:border-l border-border/50 sm:pl-4">

          {/* Status/Score */}
          <div className="flex items-center gap-3">
            {project.status === 'analysis_complete' ? (
              (project.is_reviewed) ? (
                <div className="flex items-center gap-1.5 bg-primary/10 text-primary px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide whitespace-nowrap">
                  <span>Recommendations ready</span>
                  <ArrowRight className="w-3 h-3" />
                </div>
              ) : (
                <div className="flex items-center gap-1.5 bg-yellow-50 text-yellow-700 border border-yellow-200 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide whitespace-nowrap">
                  <Clock className="w-3 h-3" />
                  <span>Pending Review</span>
                </div>
              )
            ) : (
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-medium ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border} border uppercase tracking-wide whitespace-nowrap`}>
                {statusConfig.animate && (
                  <span className="relative flex h-1.5 w-1.5">
                    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${statusConfig.text} opacity-75`}></span>
                    <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${statusConfig.indicatorColor}`}></span>
                  </span>
                )}
                {statusConfig.label}
              </span>
            )}

            {/* Score Label */}
            {!(project.status === 'analysis_complete' && !project.is_reviewed) && project.status === 'analysis_complete' && projectStatus && (
              <span className={`hidden lg:inline-block text-sm font-bold font-cabinet uppercase tracking-wide ${projectStatus.color} whitespace-nowrap`}>
                {projectStatus.label}
              </span>
            )}
          </div>

          {/* Delete Menu */}
          {isOwner && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <button className="p-1 text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-secondary">
                  <MoreVertical className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive cursor-pointer"
                  onClick={handleDeleteClick}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Project
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{project.title}"? This action cannot be undone and will permanently delete all associated data including scripts and scores.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
